<template>
  <div>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card elevation="3" rounded="xl">
            <!-- Page Header -->
            <v-card-title class="pb-3 pt-5 px-6">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <h2 class="text-h5 font-weight-bold primary--text mb-1">
                    Support Tickets
                  </h2>
                  <p class="text-body-2 grey--text ma-0">
                    Manage and respond to customer support requests
                  </p>
                </v-col>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end align-center">
                    <v-btn
                      color="primary"
                      elevation="2"
                      rounded
                      @click="getData"
                      :loading="isLoading"
                      class="px-5"
                    >
                      <v-icon left small>mdi-refresh</v-icon>
                      Refresh
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider class="mx-6"></v-divider>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-2 pt-4 px-6">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-3" color="primary">mdi-filter-variant</v-icon>
                    <span class="text-subtitle-1 font-weight-medium">Filter Tickets</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-btn-toggle
                      v-model="viewMode"
                      color="primary"
                      dense
                      rounded
                    >
                      <v-btn small value="table">
                        <v-icon>mdi-view-list</v-icon>
                      </v-btn>
                      <v-btn small value="cards">
                        <v-icon>mdi-view-grid</v-icon>
                      </v-btn>
                    </v-btn-toggle>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-6 pb-6">
              <v-row class="align-center">
                <!-- Search Field -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="search_keyword"
                    label="Search Tickets"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    rounded
                    dense
                    clearable
                    placeholder="Ticket code, title, user..."
                    @click:clear="(search_keyword = ''), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" sm="6" md="2">
                  <v-select
                    v-model="filter.status"
                    :items="statusItems"
                    label="Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    outlined
                    rounded
                    dense
                    clearable
                    @change="(page = 1), getData()"
                  ></v-select>
                </v-col>

                <!-- Month Filter -->
                <v-col cols="12" sm="6" md="3">
                  <v-menu
                    v-model="month_menu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-model="filter.month_year"
                        label="Filter by Month"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        rounded
                        dense
                        v-bind="attrs"
                        v-on="on"
                        clearable
                        placeholder="Select month"
                        @click:clear="(filter.month_year = ''), getData()"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.month_year"
                      type="month"
                      @change="(month_menu = false), getData()"
                    ></v-date-picker>
                  </v-menu>
                </v-col>

                <!-- Priority Filter -->
                <v-col cols="12" md="4">
                  <v-chip-group
                    v-model="selectedPriority"
                    column
                    multiple
                    @change="getData"
                  >
                    <v-chip
                      filter
                      outlined
                      small
                      color="error"
                    >
                      <v-icon left small>mdi-alert</v-icon>
                      High Priority
                    </v-chip>
                  </v-chip-group>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Tickets Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" rounded="lg">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-4 px-6">
              <v-row class="w-100 ma-0" align="center">
                <!-- Title Section -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="24">mdi-table</v-icon>
                    <span class="text-h6 font-weight-medium">Tickets List</span>
                    <v-chip class="ml-3" color="primary" outlined small>
                      {{ count }} {{ count === 1 ? "Ticket" : "Tickets" }} Found
                    </v-chip>
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text" v-if="count > 0">
                      Showing {{ (page - 1) * rowsPerPage + 1 }} -
                      {{ Math.min(page * rowsPerPage, count) }} of {{ count }}
                    </span>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <table-skeleton v-if="isLoading" :rows="8" :cols="6" />

              <!-- Table View -->
              <div v-if="viewMode === 'table' && !isLoading">
                <v-data-table
                  :headers="tableHeaders"
                  :items="tickets"
                  :server-items-length="count"
                  :page.sync="page"
                  :items-per-page="rowsPerPage"
                  hide-default-footer
                  class="enhanced-table"
                >
                  <template #[`item.status`]="{ item }">
                    <v-chip
                      :color="getStatusColor(item.status)"
                      :text-color="getStatusTextColor(item.status)"
                      small
                      class="font-weight-bold"
                    >
                      <v-icon left small>{{ getStatusIcon(item.status) }}</v-icon>
                      {{ getStatusText(item.status) }}
                    </v-chip>
                  </template>

                  <template #[`item.created_at`]="{ item }">
                    <div class="d-flex flex-column">
                      <span class="font-weight-medium">
                        {{ formatTableDate(item.created_at) }}
                      </span>
                      <span class="text-caption grey--text">
                        {{ formatTableTime(item.created_at) }}
                      </span>
                    </div>
                  </template>

                  <template #[`item.ticket_code`]="{ item }">
                    <v-chip outlined color="primary" small>
                      {{ item.ticket_code }}
                    </v-chip>
                  </template>

                  <template #[`item.user`]="{ item }">
                    <div class="d-flex align-center">
                      <v-avatar size="32" color="primary" class="mr-2">
                        <span class="white--text font-weight-bold">
                          {{ getUserInitials(item.user) }}
                        </span>
                      </v-avatar>
                      <div>
                        <div class="font-weight-medium">
                          {{ item.user ? item.user.first_name : '' }}
                        </div>
                        <div class="text-caption grey--text">
                          {{ item.user ? item.user.email : '' }}
                        </div>
                      </div>
                    </div>
                  </template>

                  <template #[`item.is_approved`]="{ item }">
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <v-icon
                          v-bind="attrs"
                          v-on="on"
                          small
                          :color="item.is_approved == 1 ? 'success' : 'error'"
                        >
                          {{ item.is_approved == 1 ? 'mdi-check-circle' : 'mdi-close-circle' }}
                        </v-icon>
                      </template>
                      <span>{{ item.is_approved == 1 ? 'Active' : 'Inactive' }}</span>
                    </v-tooltip>
                  </template>

                  <template #[`item.actions`]="{ item }">
                    <div class="d-flex justify-center">
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            small
                            color="primary"
                            rounded
                            @click="openTicketDetails(item)"
                            class="mr-2"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>mdi-eye</v-icon>
                          </v-btn>
                        </template>
                        <span>View Details</span>
                      </v-tooltip>

                      <!-- Actions Menu -->
                      <v-menu bottom left rounded>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            small
                            color="info"
                            rounded
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>

                        <v-list dense>
                          <v-list-item @click="updateTicketStatus(item, 1)">
                            <v-list-item-icon>
                              <v-icon small color="warning">mdi-progress-clock</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>In Progress</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item @click="updateTicketStatus(item, 2)">
                            <v-list-item-icon>
                              <v-icon small color="success">mdi-check-circle</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>Close</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item @click="updateTicketStatus(item, 0)">
                            <v-list-item-icon>
                              <v-icon small color="grey">mdi-pause-circle</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>Pending</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </div>
                  </template>

                  <!-- Empty State -->
                  <template #no-data>
                    <div class="text-center py-12">
                      <v-icon size="80" color="grey lighten-3">mdi-ticket-outline</v-icon>
                      <h3 class="text-h6 grey--text mt-4 mb-2">No Tickets Found</h3>
                      <p class="grey--text mb-4">
                        No tickets match your current filters. Try adjusting your search criteria.
                      </p>
                    </div>
                  </template>
                </v-data-table>
              </div>

              <!-- Cards View -->
              <div v-else-if="viewMode === 'cards' && !isLoading" class="pa-4">
                <v-row>
                  <v-col
                    v-for="(ticket, i) in tickets"
                    :key="`ticket-${i}`"
                    cols="12"
                    md="6"
                    lg="4"
                  >
                    <v-card
                      elevation="2"
                      rounded="xl"
                      class="ticket-card h-100"
                      :class="{ 'ticket-high-priority': ticket.priority === 'high' }"
                    >
                      <v-card-title class="pa-4 pb-2">
                        <div class="d-flex justify-between align-center w-100">
                          <v-chip outlined color="primary" small>
                            {{ ticket.ticket_code }}
                          </v-chip>
                          <v-chip
                            :color="getStatusColor(ticket.status)"
                            :text-color="getStatusTextColor(ticket.status)"
                            small
                            class="font-weight-bold"
                          >
                            {{ getStatusText(ticket.status) }}
                          </v-chip>
                        </div>
                      </v-card-title>

                      <v-card-text class="pa-4">
                        <h3 class="text-h6 font-weight-bold mb-2">
                          {{ ticket.title }}
                        </h3>
                        
                        <div class="d-flex align-center mb-3">
                          <v-avatar size="24" color="primary" class="mr-2">
                            <span class="white--text text-caption font-weight-bold">
                              {{ getUserInitials(ticket.user) }}
                            </span>
                          </v-avatar>
                          <div>
                            <div class="text-body-2 font-weight-medium">
                              {{ ticket.user ? ticket.user.first_name : '' }}
                            </div>
                            <div class="text-caption grey--text">
                              {{ formatTableDate(ticket.created_at) }}
                            </div>
                          </div>
                        </div>
                      </v-card-text>

                      <v-card-actions class="pa-4 pt-0">
                        <v-btn
                          color="primary"
                          outlined
                          small
                          @click="openTicketDetails(ticket)"
                        >
                          <v-icon left small>mdi-eye</v-icon>
                          View Details
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-menu offset-y>
                          <template #activator="{ on, attrs }">
                            <v-btn
                              icon
                              small
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                          </template>
                          <v-list dense>
                            <v-list-item @click="updateTicketStatus(ticket, 1)">
                              <v-list-item-icon>
                                <v-icon color="warning">mdi-progress-clock</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title>In Progress</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="updateTicketStatus(ticket, 2)">
                              <v-list-item-icon>
                                <v-icon color="success">mdi-check-circle</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title>Close</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="updateTicketStatus(ticket, 0)">
                              <v-list-item-icon>
                                <v-icon color="grey">mdi-pause-circle</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title>Pending</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </v-card-actions>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- Empty State for Cards View -->
                <div v-if="tickets.length === 0" class="text-center py-12">
                  <v-icon size="64" color="grey lighten-1">mdi-ticket-outline</v-icon>
                  <h2 class="text-h5 grey--text mt-4 mb-2">No tickets found</h2>
                  <p class="grey--text">Try adjusting your search filters</p>
                </div>
              </div>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-4 px-6">
              <v-row class="w-100 ma-0" align="center">
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-body-2 grey--text font-weight-medium">
                      Showing {{ (page - 1) * rowsPerPage + 1 }} to
                      {{ Math.min(page * rowsPerPage, count) }} of
                      {{ count }} tickets
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      circle
                      class="mx-2"
                    ></v-pagination>
                  </div>
                </v-col>

                <!-- Additional Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-body-2 grey--text font-weight-medium">
                      {{ rowsPerPage }} per page
                    </span>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Enhanced Ticket Details Modal -->
      <v-dialog
        v-model="show_ticket_details"
        max-width="1200px"
        persistent
        scrollable
        transition="dialog-bottom-transition"
      >
        <v-card class="ticket-details-modal" rounded="xl">
          <v-toolbar color="primary" dark flat>
            <v-btn icon @click="closeTicketDetails">
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-toolbar-title class="text-h6 font-weight-bold">
              <v-icon left>mdi-ticket</v-icon>
              {{ ticket_title }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-chip
              :color="getStatusColor(selectedTicket.status)"
              :text-color="getStatusTextColor(selectedTicket.status)"
              class="font-weight-bold"
            >
              {{ getStatusText(selectedTicket.status) }}
            </v-chip>
            <v-switch
              v-if="selectedTicket.booking?.aggregator?.parent_id === 4"
              v-model="chatView"
              @change="getTicketDetails(selectedTicket)"
              :label="chatView ? 'ONDC CHAT' : 'USER CHAT'"
              class="ma-2"
              color="white"
            ></v-switch>
          </v-toolbar>

          <v-container class="chat-container pa-0" style="max-height: 600px; overflow-y: auto;">
            <v-progress-linear
              v-if="isLoading"
              indeterminate
              color="primary"
            ></v-progress-linear>

            <div class="pa-4">
              <template v-if="!isLoading">
                <div
                  v-for="(ticket_detail, tD) in ticket_details"
                  :key="tD"
                  class="message-container mb-4"
                  :class="ticket_detail.sender_id === user.id ? 'sent' : 'received'"
                >
                  <div class="message-bubble">
                    <div class="message-content" v-html="ticket_detail.description"></div>
                    <div class="message-meta d-flex align-center justify-end mt-2">
                      <v-icon
                        small
                        :color="ticket_detail.is_seen ? 'blue' : 'grey'"
                        class="mr-1"
                      >
                        {{ ticket_detail.is_seen ? "mdi-check-all" : "mdi-check" }}
                      </v-icon>
                      <span class="text-caption">
                        {{ formatMessageDate(ticket_detail.created_at) }}
                      </span>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </v-container>

          <v-divider></v-divider>

          <v-card-actions class="pa-4">
            <v-row class="align-center">
              <v-col cols="9">
                <vue-editor
                  v-model="message"
                  class="message-editor"
                  placeholder="Type your message here..."
                />
              </v-col>
              <v-col cols="3">
                <v-select
                  v-if="chatView && !ticket_detail.ondc_code"
                  v-model="ticket_detail.ondc_code"
                  :items="OndcStatus"
                  label="ONDC Code"
                  outlined
                  dense
                ></v-select>

                <v-btn
                  v-if="!chatView || (chatView && ticket_detail.ondc_code)"
                  :loading="isSending"
                  :disabled="isSending || !message.trim()"
                  color="primary"
                  large
                  rounded
                  block
                  @click="sendMessage(ticket_details)"
                >
                  <v-icon left>mdi-send</v-icon>
                  Send Message
                </v-btn>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Enhanced Image Gallery Modal -->
      <v-dialog v-model="TicketsImageDialog" max-width="1000px">
        <v-card rounded="xl">
          <v-toolbar color="dark" dark flat>
            <v-toolbar-title>
              <v-icon left>mdi-image-multiple</v-icon>
              Ticket Images
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="TicketsImageDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-carousel
            height="600"
            hide-delimiter-background
            show-arrows-on-hover
            class="rounded-b-xl"
          >
            <v-carousel-item v-for="(slide, i) in slides" :key="i">
              <v-sheet height="100%" class="d-flex align-center justify-center">
                <v-img
                  :src="mediaUrl + slide"
                  contain
                  height="600"
                  max-width="1000"
                />
              </v-sheet>
            </v-carousel-item>
          </v-carousel>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import axios from "axios";
import { VueEditor } from "vue2-editor";

export default {
  components: {
    VueEditor,
  },
  name: "ticketsPage",
  data() {
    return {
      viewMode: 'table',
      selectedPriority: [],
      filter: {
        month_year: "",
        month: "",
        year: "",
        expense_id: "",
      },
      chatView: false,
      items: [
        { text: "Send To User", value: 1 },
        { text: "Send To Ondc Partner", value: 2 },
      ],
      OndcStatus: [
        { text: "INFO_PROVIDED", value: "INFO_PROVIDED" },
        { text: "RESOLUTION_ACCEPTED", value: "RESOLUTION_ACCEPTED" },
        { text: "OPEN", value: "OPEN" },
      ],
      message: "",
      tickets: [],
      ticket_details: [],
      tableHeaders: [
        { text: 'Status', value: 'is_approved', width: '80px', sortable: false },
        { text: 'Ticket Code', value: 'ticket_code', width: '120px' },
        { text: 'Title', value: 'title', width: '300px' },
        { text: 'Created By', value: 'user', width: '200px', sortable: false },
        { text: 'Status', value: 'status', width: '120px' },
        { text: 'Date', value: 'created_at', width: '150px' },
        { text: 'Actions', value: 'actions', width: '120px', sortable: false },
      ],
      statusItems: [
        { id: 1, text: "IN PROGRESS", value: 1 },
        { id: 2, text: "CLOSE", value: 2 },
        { id: 3, text: "PENDING", value: 0 },
      ],
      page: 1,
      serialNoStarting: 1,
      paginationLength: 0,
      selectedTicket: {},
      count: 0,
      search_keyword: "",
      show_ticket_details: false,
      ticket_detail: {
        sent_to: 0,
        ondc_code: null,
      },
      month_menu: false,
      isLoading: false,
      TicketsImageDialog: false,
      isSending: false,
      slides: [],
      ticketId: "",
      isSeen: false,
      ticket_status: "",
      ticket_user_Id: "",
      ticket_title: "",
    };
  },
  watch: {
    chatView(newVal) {
      if (newVal == true) {
        this.ticket_detail.sent_to = 2;
      } else {
        this.ticket_detail.sent_to = 1;
      }
    },
    page: "getData",
    month_menu: "getMonthYear",
  },
  mounted() {
    console.log("logged in user", this.user.id);
    this.getData();
  },
  methods: {
    getStatusColor(ticketStatus) {
      const colors = {
        0: 'orange',
        1: 'warning',
        2: 'success'
      };
      return colors[ticketStatus] || 'grey';
    },
    
    getStatusTextColor() {
      return 'white';
    },
    
    getStatusText(status) {
      const texts = {
        0: 'PENDING',
        1: 'IN PROGRESS',
        2: 'CLOSED'
      };
      return texts[status] || 'UNKNOWN';
    },
    
    getStatusIcon(status) {
      const icons = {
        0: 'mdi-pause-circle',
        1: 'mdi-progress-clock',
        2: 'mdi-check-circle'
      };
      return icons[status] || 'mdi-help-circle';
    },
    
    getUserInitials(user) {
      if (!user || !user.first_name) return '?';
      return user.first_name.charAt(0).toUpperCase() + 
             (user.last_name ? user.last_name.charAt(0).toUpperCase() : '');
    },
    
    formatMessageDate(dateString) {
      if (!dateString) return '';
      
      const messageDate = new Date(dateString);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
      const messageDay = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
      
      const timeString = messageDate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      
      if (messageDay.getTime() === today.getTime()) {
        return `Today ${timeString}`;
      } else if (messageDay.getTime() === yesterday.getTime()) {
        return `Yesterday ${timeString}`;
      } else if (now.getTime() - messageDate.getTime() < 7 * 24 * 60 * 60 * 1000) {
        const dayName = messageDate.toLocaleDateString('en-US', { weekday: 'long' });
        return `${dayName} ${timeString}`;
      } else {
        const dateString = messageDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: messageDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
        return `${dateString} ${timeString}`;
      }
    },
    
    formatTableDate(dateString) {
      if (!dateString) return '';
      
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
      });
    },
    
    formatTableTime(dateString) {
      if (!dateString) return '';
      
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },
    
    openTicketDetails(ticket) {
      this.getTicketDetails(ticket);
      this.UpdateIsSeen(ticket);
      this.show_ticket_details = true;
    },
    
    closeTicketDetails() {
      this.show_ticket_details = false;
      this.ticket_detail.sent_to = 0;
      this.ticket_detail.ondc_code = null;
    },
    
    async updateTicketStatus(ticket, status) {
      try {
        ticket.status = status;
        ticket.send_by_id = this.user.id;
        await axios.patch(`/tickets/${ticket.id}`, ticket);
        
        this.$store.dispatch('showSnackbar', {
          message: 'Ticket status updated successfully',
          color: 'success'
        });
      } catch (error) {
        this.$store.dispatch('showSnackbar', {
          message: 'Error updating ticket status',
          color: 'error'
        });
      }
    },

    async getData() {
      this.isLoading = true;
      if (this.filter.month_year) {
        this.filter.year = this.filter.month_year.split("-")[0];
        this.filter.month = this.filter.month_year.split("-")[1];
      } else {
        this.filter.year = "";
        this.filter.month = "";
      }
      this.search_keyword = this.search_keyword ? this.search_keyword : "";
      this.filter.user_id = this.filter.user_id ? this.filter.user_id : "";
      this.filter.status =
        this.filter.status !== undefined ? this.filter.status : "";

      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&search_keyword=" +
        this.search_keyword +
        "&month=" +
        this.filter.month +
        "&year=" +
        this.filter.year +
        "&status=" +
        this.filter.status +
        "&user_id=" +
        this.filter.user_id;
      let response = await axios.get(`tickets?${query}`);
      this.tickets = response.data.data;
      this.count = response.data.count;
      this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
      this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      this.isLoading = false;
    },
    getMonthYear() {
      if (this.filter.month_year) {
        this.filter.year = this.filter.month_year.split("-")[0];
        this.filter.month = this.filter.month_year.split("-")[1];
      }
    },
    viewImage(ticketId) {
      const ticket = this.tickets.find((ticket) => ticket.id === ticketId);
      if (ticket) {
        const imagePaths = [
          ticket.image_path,
          ticket.image_path_2,
          ticket.image_path_3,
          ticket.image_path_4,
        ];
        this.slides = imagePaths.filter((path) => path);
        this.TicketsImageDialog = true;
        console.log(this.slides);
      } else {
        console.log(`Ticket with ID ${ticketId} not found.`);
      }
    },
    async UpdateStatus(ticket) {
      console.log("tickets", ticket.id);
      console.log("status", ticket.status);
      ticket.send_by_id = this.user.id;
      await axios.patch(`/tickets/${ticket.id}`, ticket);
    },
    async sendMessage() {
      try {
        this.isSending = true;
        this.ticket_detail.ticket_id = this.ticketId;
        this.ticket_detail.description = this.message;
        this.ticket_detail.sender_id = this.user.id;
        this.ticket_detail.receiver_id = this.ticket_user_Id;
        let ticket = this.tickets.find((ticket) => ticket.id == this.ticketId);

        if (ticket) {
          this.ticket_detail.booking_id = ticket.booking_id;
        } else {
          console.error("Ticket not found");
        }

        console.log("ticket_detail", this.ticket_detail);

        let response = await axios.post("/ticket_details", this.ticket_detail);
        console.log("response", response);

        if (response) {
          let detail = response.data.data;
          this.ticket_details.push(detail);
          this.isSending = false;
          this.message = "";
          this.ticket_detail.ondc_code = null;
        }
        this.$refs.chatBody.scrollTop = this.$refs.chatBody.scrollHeight;
      } catch (e) {
        console.log(e);
        this.isSending = false;
      }
    },
    async getTicketDetails(ticket) {
      console.log("this.ticketId", this.ticketId);

      this.isLoading = true;
      this.isSeen = true;
      this.ticketId = ticket.id;
      this.ticket_user_Id = ticket.user_id;
      this.ticket_status = ticket.status;
      this.ticket_title = ticket.title;
      this.selectedTicket = ticket;
      let query =
        "ticket_id=" +
        ticket.id +
        "&is_admin=true" +
        "&ondc_chat=" +
        this.chatView;
      let response = await axios.get(`/ticket_details?${query}`);
      this.ticket_details = response.data.data;
      console.log("ticket_details", this.ticket_details);

      this.isLoading = false;
    },
    async UpdateIsSeen(ticket) {
      console.log("ticket IsSeen ", ticket);
      this.isLoading = true;
      let query = "ticket_id=" + ticket.id + "&user_id=" + this.user.id;
      console.log("qury ", query);
      let response = await axios.get(`/ticket_details/updateStatus?${query}`);
      console.log("updateStatus", response);

      this.isLoading = false;
    },
  },
};
</script>

<style scoped>
/* Enhanced Table Styles */
.enhanced-table >>> tbody tr {
  transition: all 0.3s ease;
}

.enhanced-table >>> tbody tr:hover {
  background-color: #f8f9fa !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.enhanced-table >>> tbody tr:nth-child(odd) {
  background-color: #fafbfc;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px 12px 0 0;
}

.enhanced-table >>> .v-data-table-header th {
  font-weight: 600 !important;
  color: #2c3e50 !important;
  border-bottom: 2px solid #dee2e6 !important;
  padding: 16px 12px !important;
  font-size: 0.875rem !important;
}

.enhanced-table >>> tbody td {
  padding: 16px 12px !important;
  border-bottom: 1px solid #f1f3f4 !important;
}

/* Card Enhancements */
.v-card {
  border-radius: 12px !important;
  border: 1px solid #f1f3f4;
}

.v-card--outlined {
  border: 2px solid #e3f2fd !important;
}

/* Ticket Card Specific Styles */
.ticket-card {
  transition: all 0.3s ease;
  background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid rgba(255, 255, 255, 0.8);
  animation: fadeInUp 0.6s ease-out;
}

.ticket-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
}

.ticket-high-priority {
  border-left: 4px solid #f44336;
}

/* Chip Enhancements */
.v-chip {
  font-weight: 500;
  border-radius: 8px !important;
}

.v-chip--pill {
  border-radius: 50px !important;
}

/* Button Enhancements */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.02em;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.v-btn--rounded {
  border-radius: 8px !important;
}

/* Input Field Enhancements */
.v-text-field--outlined >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.v-text-field--outlined.v-input--is-focused >>> .v-input__control > .v-input__slot {
  border-color: #1976d2 !important;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

.v-select--outlined >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

/* Avatar Enhancements */
.v-avatar {
  border: 2px solid #f1f3f4;
  transition: all 0.3s ease;
}

.v-avatar:hover {
  border-color: #1976d2;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
}

/* Pagination Enhancements */
.v-pagination >>> .v-pagination__item {
  border-radius: 8px !important;
  margin: 0 2px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.v-pagination >>> .v-pagination__item:hover {
  background-color: #e3f2fd !important;
  transform: translateY(-1px);
}

.v-pagination >>> .v-pagination__item--active {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.4);
}

/* Menu Enhancements */
.v-menu__content {
  border-radius: 12px !important;
  border: 1px solid #f1f3f4;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
}

.v-list-item {
  transition: all 0.3s ease;
  border-radius: 6px;
  margin: 2px 4px;
}

.v-list-item:hover {
  background-color: #f8f9fa !important;
  transform: translateX(4px);
}

/* Chat Specific Styles */
.ticket-details-modal {
  background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%);
}

.chat-container {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.message-container {
  display: flex;
  margin-bottom: 1rem;
}

.message-container.sent {
  justify-content: flex-end;
}

.message-container.received {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 75%;
  padding: 1rem;
  border-radius: 18px;
  position: relative;
  word-wrap: break-word;
}

.message-container.sent .message-bubble {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-bottom-right-radius: 4px;
}

.message-container.received .message-bubble {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  border-bottom-left-radius: 4px;
}

.message-content {
  margin-bottom: 0.5rem;
}

.message-meta {
  font-size: 0.75rem;
  opacity: 0.8;
}

.message-editor {
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
}

.message-editor >>> .ql-container {
  border: none;
  min-height: 100px;
}

.message-editor >>> .ql-toolbar {
  border: none;
  border-bottom: 1px solid #e0e0e0;
}

/* Loading Animation */
@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

.v-progress-circular {
  animation: pulse 2s infinite;
}

/* Enhanced animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Dialog Enhancements */
.v-dialog >>> .v-card {
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2) !important;
}

/* Responsive design */
@media (max-width: 960px) {
  .enhanced-table >>> tbody td {
    padding: 12px 8px !important;
  }

  .v-card-title {
    padding: 16px !important;
  }

  .v-card-text {
    padding: 16px !important;
  }

  .ticket-card {
    margin-bottom: 1rem;
  }

  .message-bubble {
    max-width: 90%;
  }
}

@media (max-width: 600px) {
  .message-bubble {
    max-width: 95%;
    padding: 0.75rem;
  }
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>